import { Handle, Position, useReactFlow } from '@xyflow/react'
import { Code, Eye, Plus, Trash2, Copy, Download, Settings } from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface HTMLField {
  id: string
  name: string
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'number' | 'email' | 'date'
  label: string
  placeholder?: string
  required?: boolean
  options?: string[]
  defaultValue?: string
  validation?: string
  cssClass?: string
}

interface NodeHTMLFieldsData {
  label: string
  informationClassification: string
  createdBy: string
  createdAt: string
  fieldSetName: string
  htmlTemplate: string
  fieldType: 'Input Form' | 'Display Template' | 'Interactive Widget'
  cssClasses: string
  fields: HTMLField[]
  statusMessage: string
  isMinimized: boolean
  isLocked: boolean
  generatedHTML?: string
  previewMode?: boolean
}

interface NodeHTMLFieldsProps {
  id: string
  data: NodeHTMLFieldsData
  selected?: boolean
}

export default function NodeHTMLFields({ id, data, selected }: NodeHTMLFieldsProps) {
  const { updateNodeData } = useReactFlow()
  const { toast } = useToast()
    // ✅ Initialize with safe defaults, sync from collaborative state in useEffect
  const [fieldSetName, setFieldSetName] = useState('')
  const [htmlTemplate, setHtmlTemplate] = useState('')
  const [fieldType, setFieldType] = useState<'Input Form' | 'Display Template' | 'Interactive Widget'>('Input Form')
  const [cssClasses, setCssClasses] = useState('')
  const [fields, setFields] = useState<HTMLField[]>([])
  const [previewMode, setPreviewMode] = useState(false)
  const [generatedHTML, setGeneratedHTML] = useState('')

  // ✅ Sync local state from collaborative state
  useEffect(() => {
    setFieldSetName(data.fieldSetName || '')
  }, [data.fieldSetName])

  useEffect(() => {
    setHtmlTemplate(data.htmlTemplate || '')
  }, [data.htmlTemplate])

  useEffect(() => {
    setFieldType(data.fieldType || 'Input Form')
  }, [data.fieldType])

  useEffect(() => {
    setCssClasses(data.cssClasses || '')
  }, [data.cssClasses])

  useEffect(() => {
    setFields(data.fields || [])
  }, [data.fields])

  useEffect(() => {
    setPreviewMode(data.previewMode || false)
  }, [data.previewMode])

  useEffect(() => {
    setGeneratedHTML(data.generatedHTML || '')
  }, [data.generatedHTML])

  // ✅ Collaborative sync function - only call on explicit user actions
  const syncNodeData = useCallback((updates: Partial<NodeHTMLFieldsData>) => {
    updateNodeData(id, { ...data, ...updates })
  }, [id, data, updateNodeData])

  // Generate HTML from fields
  const generateHTML = useCallback(() => {
    if (fields.length === 0) {
      toast({
        title: 'No Fields',
        description: 'Please add some fields first',
        variant: 'destructive'
      })
      return
    }

    let html = ''
    
    if (fieldType === 'Input Form') {
      html = `<form class="html-fields-form ${cssClasses}">\n`
      html += `  <h2>${fieldSetName || 'Form'}</h2>\n`
      
      fields.forEach(field => {
        html += `  <div class="field-group ${field.cssClass || ''}">\n`
        html += `    <label for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>\n`
        
        switch (field.type) {
          case 'textarea':
            html += `    <textarea id="${field.name}" name="${field.name}"${field.required ? ' required' : ''}${field.placeholder ? ` placeholder="${field.placeholder}"` : ''}>${field.defaultValue || ''}</textarea>\n`
            break
          case 'select':
            html += `    <select id="${field.name}" name="${field.name}"${field.required ? ' required' : ''}>\n`
            field.options?.forEach(option => {
              html += `      <option value="${option}"${field.defaultValue === option ? ' selected' : ''}>${option}</option>\n`
            })
            html += `    </select>\n`
            break
          case 'checkbox':
            html += `    <input type="checkbox" id="${field.name}" name="${field.name}"${field.defaultValue === 'true' ? ' checked' : ''} value="true">\n`
            break
          case 'radio':
            field.options?.forEach((option, index) => {
              html += `    <input type="radio" id="${field.name}_${index}" name="${field.name}" value="${option}"${field.defaultValue === option ? ' checked' : ''}>\n`
              html += `    <label for="${field.name}_${index}">${option}</label>\n`
            })
            break
          default:
            html += `    <input type="${field.type}" id="${field.name}" name="${field.name}"${field.required ? ' required' : ''}${field.placeholder ? ` placeholder="${field.placeholder}"` : ''}${field.defaultValue ? ` value="${field.defaultValue}"` : ''}>\n`
        }
        html += `  </div>\n`
      })
      
      html += `  <button type="submit">Submit</button>\n`
      html += `</form>`
    } else if (fieldType === 'Display Template') {
      html = htmlTemplate || `<div class="html-fields-display ${cssClasses}">\n  <h2>${fieldSetName || 'Display'}</h2>\n  <!-- Add your display template here -->\n</div>`
    } else {
      html = htmlTemplate || `<div class="html-fields-widget ${cssClasses}">\n  <h2>${fieldSetName || 'Widget'}</h2>\n  <!-- Add your interactive widget here -->\n</div>`
    }    setGeneratedHTML(html)
    syncNodeData({ generatedHTML: html })
    toast({
      title: 'HTML Generated',
      description: 'HTML code has been generated successfully'
    })
  }, [fields, fieldType, fieldSetName, cssClasses, htmlTemplate, syncNodeData, toast])
  // Add new field
  const addField = useCallback(() => {
    const newField: HTMLField = {
      id: `field-${Date.now()}`,
      name: `field_${fields.length + 1}`,
      type: 'text',
      label: `Field ${fields.length + 1}`,
      required: false
    }
    const newFields = [...fields, newField]
    setFields(newFields)
    syncNodeData({ fields: newFields })
  }, [fields, syncNodeData])
  // Remove field
  const removeField = useCallback((fieldId: string) => {
    const newFields = fields.filter(f => f.id !== fieldId)
    setFields(newFields)
    syncNodeData({ fields: newFields })
  }, [fields, syncNodeData])
  // Update field
  const updateField = useCallback((fieldId: string, updates: Partial<HTMLField>) => {
    const newFields = fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    setFields(newFields)
    syncNodeData({ fields: newFields })
  }, [fields, syncNodeData])

  // Copy HTML to clipboard
  const copyHTML = useCallback(() => {
    if (generatedHTML) {
      navigator.clipboard.writeText(generatedHTML)
      toast({
        title: 'Copied',
        description: 'HTML code copied to clipboard'
      })
    }
  }, [generatedHTML, toast])

  // Export HTML file
  const exportHTML = useCallback(() => {
    if (generatedHTML) {
      const blob = new Blob([generatedHTML], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fieldSetName || 'html-fields'}.html`
      a.click()
      URL.revokeObjectURL(url)
      
      toast({
        title: 'Exported',
        description: 'HTML file has been downloaded'
      })
    }
  }, [generatedHTML, fieldSetName, toast])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      selected={selected}
      className="min-w-[900px] min-h-[630px]"
    >
      <Handle type="target" position={Position.Left} />
      
      <div className="flex flex-col h-full space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Code className="h-5 w-5 text-pink-600" />
            <span className="font-semibold">HTML Fields</span>
            <Badge variant="outline">{fieldType}</Badge>
          </div>
          <div className="flex items-center space-x-2">            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newPreviewMode = !previewMode
                setPreviewMode(newPreviewMode)
                syncNodeData({ previewMode: newPreviewMode })
              }}
            >
              <Eye className="h-4 w-4 mr-1" />
              {previewMode ? 'Edit' : 'Preview'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              generateHTML()
              // generateHTML function will update generatedHTML state and sync
            }}>
              <Settings className="h-4 w-4 mr-1" />
              Generate
            </Button>
          </div>
        </div>        {!previewMode ? (
          <div className="flex-1 grid grid-cols-2 gap-4">
            {/* Configuration Panel */}
            <Card className="h-fit">
              <CardHeader className="pb-3" hasCardAction={false}>
                <CardTitle className="text-sm">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="fieldSetName">Field Set Name</Label>                  <Input
                    id="fieldSetName"
                    value={fieldSetName}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setFieldSetName(newValue)
                      syncNodeData({ fieldSetName: newValue })
                    }}
                    placeholder="Enter field set name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fieldType">Field Type</Label>
                  <Select value={fieldType} onValueChange={(value: any) => {
                    setFieldType(value)
                    syncNodeData({ fieldType: value })
                  }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Input Form">Input Form</SelectItem>
                      <SelectItem value="Display Template">Display Template</SelectItem>
                      <SelectItem value="Interactive Widget">Interactive Widget</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="cssClasses">CSS Classes</Label>                  <Input
                    id="cssClasses"
                    value={cssClasses}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setCssClasses(newValue)
                      syncNodeData({ cssClasses: newValue })
                    }}
                    placeholder="CSS classes for styling"
                  />
                </div>

                {fieldType !== 'Input Form' && (
                  <div>
                    <Label htmlFor="htmlTemplate">HTML Template</Label>                    <Textarea
                      id="htmlTemplate"
                      value={htmlTemplate}
                      onChange={(e) => {
                        const newValue = e.target.value
                        setHtmlTemplate(newValue)
                        syncNodeData({ htmlTemplate: newValue })
                      }}
                      placeholder="Enter custom HTML template"
                      rows={6}
                    />
                  </div>
                )}
              </CardContent>
            </Card>            {/* Fields Panel */}
            {fieldType === 'Input Form' && (
              <Card className="h-fit">
                <CardHeader className="pb-3" hasCardAction={false}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Fields ({fields.length})</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => {
                      addField()
                      // Sync after fields update (done in addField)
                    }}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Field
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                  {fields.map((field, index) => (
                    <div key={field.id} className="border rounded p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Field {index + 1}</span>                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            removeField(field.id)
                            // Sync after fields update (done in removeField)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Name</Label>                          <Input
                            value={field.name}
                            onChange={(e) => {
                              updateField(field.id, { name: e.target.value })
                              // Sync after fields update (done in updateField)
                            }}
                            placeholder="field_name"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Type</Label>                          <Select
                            value={field.type}
                            onValueChange={(value: any) => {
                              updateField(field.id, { type: value })
                              // Sync after fields update (done in updateField)
                            }}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="textarea">Textarea</SelectItem>
                              <SelectItem value="select">Select</SelectItem>
                              <SelectItem value="checkbox">Checkbox</SelectItem>
                              <SelectItem value="radio">Radio</SelectItem>
                              <SelectItem value="number">Number</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="date">Date</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Label</Label>                        <Input
                          value={field.label}
                          onChange={(e) => {
                            updateField(field.id, { label: e.target.value })
                            // Sync after fields update (done in updateField)
                          }}
                          placeholder="Field Label"
                          className="h-8"
                        />
                      </div>
                      
                      {(field.type === 'select' || field.type === 'radio') && (
                        <div>
                          <Label className="text-xs">Options (comma-separated)</Label>                          <Input
                            value={field.options?.join(', ') || ''}
                            onChange={(e) => {
                              updateField(field.id, { 
                                options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                              })
                              // Sync after fields update (done in updateField)
                            }}
                            placeholder="Option 1, Option 2, Option 3"
                            className="h-8"
                          />
                        </div>
                      )}
                    </div>
                  ))}                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Preview/Output Panel */
          <Card className="flex-1">
            <CardHeader className="pb-3" hasCardAction={false}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Generated HTML</CardTitle>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" onClick={copyHTML} disabled={!generatedHTML}>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportHTML} disabled={!generatedHTML}>
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {generatedHTML ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded border">
                    <pre className="text-sm overflow-auto max-h-[300px]">
                      <code>{generatedHTML}</code>
                    </pre>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Live Preview:</h4>
                    <div 
                      className="border p-4 rounded bg-white"
                      dangerouslySetInnerHTML={{ __html: generatedHTML }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Click "Generate" to create HTML code</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      <Handle type="source" position={Position.Right} />
    </NodeBaseContainer>
  )
}
